import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import WappIcono from "../../../components/wappIcono";
import { Nodo } from "../../../services/v2/wappdrive.service";
import { Ficha } from "./ficha";
import { fecha } from "./dialogos";

const http = () => HttpManager.getInstance().wappdriveService;
const sinAcentos = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Resalta en negritas las palabras buscadas dentro del nombre (sin importar acentos ni mayúsculas). */
export const Resaltar: React.FC<{ texto: string; q: string }> = ({ texto, q }) => {
	const pals = q.trim().split(/\s+/).filter((p) => p.length > 0).map(sinAcentos);
	if (!pals.length) return <>{texto}</>;
	const base = sinAcentos(texto);
	const marca = new Array(texto.length).fill(false);
	pals.forEach((p) => { let i = base.indexOf(p); while (i >= 0) { for (let k = i; k < i + p.length; k++) marca[k] = true; i = base.indexOf(p, i + p.length); } });
	const trozos: React.ReactNode[] = [];
	let i = 0;
	while (i < texto.length) {
		let j = i;
		while (j < texto.length && marca[j] === marca[i]) j++;
		trozos.push(marca[i] ? <b key={i}>{texto.slice(i, j)}</b> : <React.Fragment key={i}>{texto.slice(i, j)}</React.Fragment>);
		i = j;
	}
	return <>{trozos}</>;
};

/**
 * El buscador de WappDrive, como el de Google Drive: sugerencias mientras escribes (flechas + Enter), "Ver todos los
 * resultados", borrar con la ✕, Esc para cerrar y la tecla "/" para enfocarlo desde cualquier parte.
 */
const Buscador: React.FC<{ campusId: number; valor: string; onValor: (v: string) => void; onBuscar: (q: string) => void; onAbrir: (n: Nodo) => void; placeholder?: string }> =
	({ campusId, valor, onValor, onBuscar, onAbrir, placeholder = "Buscar en WappDrive" }) => {
	const [sug, setSug] = useState<Nodo[]>([]);
	const [abierto, setAbierto] = useState(false);
	const [cargando, setCargando] = useState(false);
	const [fallo, setFallo] = useState(false);
	const [sel, setSel] = useState(-1);
	const input = useRef<HTMLInputElement>(null);
	const caja = useRef<HTMLFormElement>(null);

	useEffect(() => {
		const q = valor.trim();
		if (q.length < 2) { setSug([]); setCargando(false); return; }
		setCargando(true);
		setFallo(false);
		let vivo = true;
		const t = setTimeout(() => http().buscar(campusId, q, { limite: 8 }).then((r) => { if (vivo) { setSug(r.data.items); setSel(-1); } }).catch(() => { if (vivo) { setSug([]); setFallo(true); } }).finally(() => vivo && setCargando(false)), 220);
		return () => { vivo = false; clearTimeout(t); };
	}, [valor, campusId]);

	useEffect(() => {
		const tecla = (e: KeyboardEvent) => {
			const el = e.target as HTMLElement;
			if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) && !el.isContentEditable) { e.preventDefault(); input.current?.focus(); }
		};
		const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
		document.addEventListener("keydown", tecla);
		document.addEventListener("mousedown", fuera);
		return () => { document.removeEventListener("keydown", tecla); document.removeEventListener("mousedown", fuera); };
	}, []);

	const buscarTodo = () => { const q = valor.trim(); if (q.length >= 2) { setAbierto(false); input.current?.blur(); onBuscar(q); } };
	const elegir = (n: Nodo) => { setAbierto(false); input.current?.blur(); onAbrir(n); };
	const teclas = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowDown") { e.preventDefault(); setAbierto(true); setSel((s) => Math.min(s + 1, sug.length)); }
		else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, -1)); }
		else if (e.key === "Escape") { setAbierto(false); input.current?.blur(); }
		else if (e.key === "Enter" && sel >= 0 && sel < sug.length) { e.preventDefault(); elegir(sug[sel]); }
	};
	const q = valor.trim();
	const mostrar = abierto && q.length >= 2;

	return (
		<form ref={caja} className={"wd-busca" + (mostrar ? " abierto" : "")} role="search" onSubmit={(e) => { e.preventDefault(); buscarTodo(); }}>
			<WappIcono n="search" className="wd-busca-lupa" />
			<input ref={input} type="text" placeholder={placeholder} value={valor} onChange={(e) => { onValor(e.target.value); setAbierto(true); }} onFocus={() => setAbierto(true)} onKeyDown={teclas}
				aria-label={placeholder} autoComplete="off" spellCheck={false} />
			{valor ? <button type="button" className="wd-busca-x" onClick={() => { onValor(""); setSug([]); input.current?.focus(); }} aria-label="Borrar búsqueda"><WappIcono n="x" /></button> : <kbd className="wd-busca-atajo" title="Presiona / para buscar">/</kbd>}
			{mostrar ? (
				<div className="wd-busca-lista" role="listbox">
					{sug.map((n, i) => (
						<button type="button" key={n.id} role="option" aria-selected={i === sel} className={i === sel ? "sel" : ""} onMouseEnter={() => setSel(i)} onClick={() => elegir(n)}>
							<Ficha n={n} />
							<span className="wd-busca-txt"><span className="wd-busca-nom"><Resaltar texto={n.name} q={q} /></span><small>{n.ruta || ""}</small></span>
							<small className="wd-busca-fecha">{fecha(n.updated_at)}</small>
						</button>
					))}
					{!sug.length ? <div className="wd-busca-vacio">{cargando ? "Buscando…" : fallo ? "No se pudo buscar. Intenta de nuevo." : "Sin coincidencias en los nombres"}</div> : null}
					<button type="button" className={"wd-busca-todo" + (sel === sug.length ? " sel" : "")} onMouseEnter={() => setSel(sug.length)} onClick={buscarTodo}>
						<WappIcono n="search" /> <span>Ver todos los resultados de “{q}”</span>
					</button>
				</div>
			) : null}
		</form>
	);
};

export default Buscador;
